import React from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import EditLayout, { pickEditLayoutHeaderTitle } from './EditLayout';
import EditProfileLayout from './EditProfileLayout';
import RecordViewNewRecordPicker, {
  fetchRecordViewChoicesForNewRecord,
  resolveEmitPayload,
} from './RecordViewNewRecordPicker';
import { theme } from '../../theme/theme.js';
import AppToast from '../AppToast';
import './css/EditLayout.css';
import './css/EditLayoutModal.css';

const EditLayoutModal = ({ show, onHide, objectName, id, isClone = false, onSave, onCancel, sourcePage, queryParams, onRefresh, useProfileLayout = false }) => {
  const [formRef, setFormRef] = React.useState(null);
  const [isSaveAndNew, setIsSaveAndNew] = React.useState(false);
  const rvPickerRef = React.useRef(null);
  const [rvPickUi, setRvPickUi] = React.useState({ loading: true, canConfirm: false });
  const [recordViewExtras, setRecordViewExtras] = React.useState({
    recordviewId: '',
    editLayoutId: '',
  });
  const [rvResolvedOnce, setRvResolvedOnce] = React.useState(false);
  const [pickRequired, setPickRequired] = React.useState(null);
  const [rvMultiOptions, setRvMultiOptions] = React.useState(null);
  const [toast, setToast] = React.useState({
    show: false,
    message: '',
    variant: 'success'
  });
  /** Modal title: wait for EditLayout fetch so we never flash the API object name */
  const [editHeaderState, setEditHeaderState] = React.useState({
    phase: 'idle',
    header: null,
  });

  const handleEditHeaderLoaded = React.useCallback(info => {
    if (!info || typeof info !== 'object') return;
    if (info.phase === 'start') {
      setEditHeaderState({ phase: 'loading', header: null });
    } else if (info.phase === 'end') {
      setEditHeaderState({ phase: 'ready', header: info.header ?? null });
    }
  }, []);

  const showRvPicker =
    !useProfileLayout &&
    !id &&
    !isClone &&
    objectName &&
    String(objectName).toLowerCase() !== 'email_log';

  React.useEffect(() => {
    if (!show) {
      setRecordViewExtras({ recordviewId: '', editLayoutId: '' });
      setRvResolvedOnce(false);
      setRvPickUi({ loading: true, canConfirm: false });
      setPickRequired(null);
      setRvMultiOptions(null);
      setEditHeaderState({ phase: 'idle', header: null });
    }
  }, [show]);

  React.useEffect(() => {
    if (!show) return;
    setEditHeaderState({ phase: 'idle', header: null });
  }, [show, objectName, id, isClone]);

  const handleRecordViewResolved = React.useCallback((payload) => {
    setRecordViewExtras(payload || { recordviewId: '', editLayoutId: '' });
    setRvResolvedOnce(true);
  }, []);

  React.useEffect(() => {
    if (!show || !showRvPicker || rvResolvedOnce) return;
    if (pickRequired === true) return;

    let cancelled = false;
    (async () => {
      try {
        const { allowed, fallbackLayout } = await fetchRecordViewChoicesForNewRecord(objectName);
        if (cancelled) return;
        if (allowed.length === 0) {
          handleRecordViewResolved({ recordviewId: '', editLayoutId: fallbackLayout });
        } else if (allowed.length === 1) {
          handleRecordViewResolved(resolveEmitPayload(String(allowed[0].id), ''));
        } else {
          setRvMultiOptions(allowed);
          setPickRequired(true);
        }
      } catch (e) {
        if (!cancelled) handleRecordViewResolved({ recordviewId: '', editLayoutId: '' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, showRvPicker, objectName, rvResolvedOnce, pickRequired, handleRecordViewResolved]);

  const mergedQueryParams = React.useMemo(() => {
    const base = { ...(queryParams && typeof queryParams === 'object' ? queryParams : {}) };
    if (recordViewExtras.recordviewId) {
      base.recordviewId = recordViewExtras.recordviewId;
      if (!String(recordViewExtras.editLayoutId || '').trim()) {
        delete base.editLayoutId;
      }
    }
    if (String(recordViewExtras.editLayoutId || '').trim()) {
      base.editLayoutId = String(recordViewExtras.editLayoutId).trim();
    }
    return Object.keys(base).length ? base : null;
  }, [queryParams, recordViewExtras]);

  /** Standalone /edit/... URL (new tab), aligned with modal query params when present */
  const fullPageEditUrl = React.useMemo(() => {
    if (!id || isClone || useProfileLayout) {
      return '';
    }
    const on = objectName != null ? String(objectName).trim() : '';
    const rid = String(id).trim();
    if (!on || !rid) return '';
    const base = `/edit/${encodeURIComponent(on)}/${encodeURIComponent(rid)}`;
    if (!mergedQueryParams || typeof mergedQueryParams !== 'object') {
      return base;
    }
    const qs = new URLSearchParams();
    Object.entries(mergedQueryParams).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') {
        qs.set(k, String(v));
      }
    });
    const q = qs.toString();
    return q ? `${base}?${q}` : base;
  }, [id, isClone, useProfileLayout, objectName, mergedQueryParams]);

  const handleClose = () => {
    onHide();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onHide();
    }
  };

  const handleSave = (savedData) => {
    // Determine if this is a new record or update
    const isNewRecord = !id || isClone;
    const successMessage = isNewRecord
      ? (isClone ? 'Record cloned successfully!' : 'Record created successfully!')
      : 'Record updated successfully!';

    // Show toast notification first
    setToast({
      show: true,
      message: successMessage,
      variant: 'success'
    });

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);

    if (onSave) {
      onSave(savedData);
    }
    console.log("Handle Save: ", isSaveAndNew);

    // Since EditLayoutModal is always opened in a modal context,
    // we should NOT redirect. Just close the modal and let the onSave callback
    // handle any refresh logic. This matches the behavior of ResizableTable
    // and RelatedTabsModal where records are created/edited without redirecting.

    // Close the modal - the parent component (via onSave callback) will handle refresh
    // Use a small delay to ensure toast is rendered before modal closes
    setTimeout(() => {
      onHide();
    }, 100);

    // Optionally trigger a refresh message for iframe contexts
    const recordId = savedData?.id || savedData?.data?.id || id;
    if (recordId && window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'recordSaved',
        action: 'refresh',
        objectName: objectName,
        recordId: recordId
      }, '*');
    }
  };

  const handleSaveAndNew = (savedData) => {
    console.log("$$ handleSaveAndNew");

    // Determine if this is a new record or update
    const isNewRecord = !id || isClone;
    const successMessage = isNewRecord
      ? (isClone ? 'Record cloned successfully!' : 'Record created successfully!')
      : 'Record updated successfully!';

    // Show toast notification
    setToast({
      show: true,
      message: successMessage,
      variant: 'success'
    });

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);

    if (onRefresh) {
      console.log("$$ onRefresh for relatedlist");
      onRefresh();
      return;
    }

    const refreshEvent = new CustomEvent('listViewRefresh', {
      detail: {
        objectName: objectName,
        recordId: savedData?.id || savedData?.data?.id || id,
        action: 'recordSavedAndNew'
      }
    });
    window.dispatchEvent(refreshEvent);


  };

  const triggerSave = () => {
    if (formRef) {
      formRef.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  const triggerSaveAndNew = async () => {
    console.log("triggerSaveAndNew");
    setIsSaveAndNew(true);
    if (formRef) {
      // Create a custom event with saveAndNew flag
      const saveAndNewEvent = new CustomEvent('submit', {
        bubbles: true,
        cancelable: true,
        detail: { saveAndNew: true }
      });
      formRef.dispatchEvent(saveAndNewEvent);
    }
  };

  const showRecordViewPickModal = show && showRvPicker && !rvResolvedOnce && pickRequired === true;
  const showEditFormModal =
    show &&
    (!showRvPicker ||
      rvResolvedOnce ||
      (showRvPicker && !rvResolvedOnce && pickRequired === null));
  const rvPrefetching = showRvPicker && !rvResolvedOnce && pickRequired === null;

  return (
    <>
      <Modal
        show={showRecordViewPickModal}
        onHide={handleClose}
        size="md"
        centered
        backdrop="static"
        keyboard
        className="recordview-pick-modal"
      >
        <Modal.Header closeButton closeVariant="white" className="modal-theme-header">
          <Modal.Title style={{ color: theme.colors.white }}>
            <div className="title-content">
              <div
                className="title-icon"
                style={{
                  width: '40px',
                  height: '40px',
                  background: theme.colors.white,
                  borderRadius: theme.borderRadius.lg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.primary,
                  fontSize: '16px',
                }}
              >
                <i className="fas fa-layer-group" />
              </div>
              <div className="title-text">
                <div
                  className="title-subtitle"
                  style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: theme.typography.fontWeight.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '2px',
                  }}
                >
                  New record
                </div>
                <h2
                  className="title-main"
                  style={{
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.white,
                    margin: 0,
                    lineHeight: 1.2,
                  }}
                >
                  Select Record View
                </h2>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          <p className="text-muted small mb-3">
            Choose how this record is classified for <strong>{objectName}</strong>. You can fill in the form after
            this step.
          </p>
          <RecordViewNewRecordPicker
            ref={rvPickerRef}
            confirmPlacement="none"
            onPickInteractionChange={setRvPickUi}
            objectName={objectName}
            allowedChoices={rvMultiOptions}
            initialRecordviewId={
              queryParams && queryParams.recordviewId ? String(queryParams.recordviewId) : ''
            }
            onResolved={handleRecordViewResolved}
          />
        </Modal.Body>
        <Modal.Footer className="bg-white border-top">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!rvPickUi.canConfirm || rvPickUi.loading}
            onClick={() => rvPickerRef.current?.confirm()}
          >
            Next
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditFormModal}
        onHide={handleClose}
        size="xl"
        centered
        className="edit-layout-modal"
        backdrop={false}
      >
        <Modal.Header className="modal-theme-header">
          <Modal.Title style={{ color: theme.colors.white }}>
            <div className="title-content">
              <div className="title-icon" style={{
                width: '40px',
                height: '40px',
                background: theme.colors.white,
                borderRadius: theme.borderRadius.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary,
                fontSize: '16px'
              }}>
                <i className="fas fa-plus-circle"></i>
              </div>
              <div className="title-text">
                <div className="title-subtitle" style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: theme.typography.fontWeight.medium,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '2px'
                }}>{useProfileLayout ? 'PROFILE' : (isClone ? 'CLONE' : (id ? 'EDIT' : 'NEW'))}</div>
                <h2 className="title-main" style={{
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.white,
                  margin: 0,
                  lineHeight: 1.2,
                  minHeight: '1.2em',
                  display: 'flex',
                  alignItems: 'center',
                }}>{useProfileLayout ? (
                  'Edit Profile'
                ) : editHeaderState.phase !== 'ready' ? (
                  <Spinner animation="border" size="sm" variant="light" style={{ opacity: 0.9 }} role="status">
                    <span className="visually-hidden">Loading title…</span>
                  </Spinner>
                ) : (
                  (pickEditLayoutHeaderTitle(editHeaderState.header) || objectName).toUpperCase()
                )}</h2>
              </div>
            </div>
          </Modal.Title>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="btn-close"
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>

        <Modal.Body className="modal-theme-body p-0">
          {useProfileLayout ? (
            <EditProfileLayout
              objectName={objectName}
              id={isClone ? null : id}
              onSave={handleSave}
              onCancel={handleCancel}
              hideHeader={true}
              formRef={setFormRef}
              openedInModal={true}
              profileEditMode={useProfileLayout}
            />
          ) : showRvPicker && !rvResolvedOnce && pickRequired === null ? (
            <div className="d-flex align-items-center justify-content-center gap-2 p-5 text-muted">
              <Spinner animation="border" size="sm" />
              <span>Loading…</span>
            </div>
          ) : (
            <EditLayout
              key={`edit-${objectName}-${recordViewExtras.recordviewId || 'norv'}-${recordViewExtras.editLayoutId || 'nolayout'}`}
              objectName={objectName}
              id={isClone ? null : id}
              cloneFromId={isClone ? id : null}
              onSave={handleSave}
              onSaveAndNew={handleSaveAndNew}
              onCancel={handleCancel}
              sourcePage={sourcePage}
              hideHeader={true}
              formRef={setFormRef}
              queryParams={mergedQueryParams}
              openedInModal={true}
              onHeaderLoaded={handleEditHeaderLoaded}
            />
          )}
        </Modal.Body>

        <Modal.Footer className="modal-theme-footer d-flex flex-wrap align-items-center gap-2">
          {fullPageEditUrl ? (
            <a
              href={fullPageEditUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small me-auto"
              style={{
                color: theme.colors.primary,
                textDecoration: 'underline',
                fontWeight: theme.typography.fontWeight.medium,
              }}
            >
              Open full-page editor
            </a>
          ) : (
            <span className="me-auto" aria-hidden="true" />
          )}
          <div className="footer-actions ms-auto">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              className="cancel-btn"
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                border: `1px solid ${theme.colors.borderMedium}`,
                background: theme.colors.white,
                color: theme.colors.textSecondary,
                borderRadius: theme.borderRadius.sm,
                transition: theme.transitions.normal,
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = theme.colors.light;
                e.target.style.borderColor = theme.colors.borderDark;
                e.target.style.color = theme.colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.colors.white;
                e.target.style.borderColor = theme.colors.borderMedium;
                e.target.style.color = theme.colors.textSecondary;
              }}
            >
              Cancel
            </Button>
            {(!id || isClone) && !useProfileLayout && !rvPrefetching && (
              <Button
                variant="outline-primary"
                onClick={() => {
                  // Trigger save and prepare for new record
                  triggerSaveAndNew();
                }}
                className="save-new-btn"
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  border: `1px solid ${theme.colors.secondary}`,
                  background: theme.colors.white,
                  color: theme.colors.secondary,
                  borderRadius: theme.borderRadius.sm,
                  transition: theme.transitions.normal,
                  minWidth: '100px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = theme.colors.secondary;
                  e.target.style.color = theme.colors.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = theme.colors.white;
                  e.target.style.color = theme.colors.secondary;
                }}
              >
                Save & New
              </Button>
            )}
            {!rvPrefetching && (
            <Button
              variant="primary"
              onClick={() => {
                // Trigger save
                triggerSave();
              }}
              className="save-btn"
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                background: theme.colors.secondary,
                border: `1px solid ${theme.colors.secondary}`,
                color: theme.colors.white,
                borderRadius: theme.borderRadius.sm,
                transition: theme.transitions.normal,
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = theme.colors.secondaryDark;
                e.target.style.borderColor = theme.colors.secondaryDark;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 2px 4px rgba(0, 123, 255, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.colors.secondary;
                e.target.style.borderColor = theme.colors.secondary;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Save
            </Button>
            )}

          </div>
        </Modal.Footer>
      </Modal>

      {/* AppToast for success messages - rendered outside Modal so it persists after modal closes */}
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
        delay={3000}
      />
    </>
  );
};

export default EditLayoutModal;
